import {firstValueFrom} from 'rxjs';
import {IBaseCRUDComponent} from '../../../../sdk/src/components/ibase-crud/ibase-crud.component';
import {Component} from '@angular/core';
import {CpmCUserRoleApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-common/cpmc-user-role.api-model';
import {CpmCUserRoleApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-user-role.api-service';
import {
    NewEditUserRoleModalComponent,
    NewEditUserRoleModalConfig,
    NewEditUserRoleModalResult
} from '../../modals/new-edit-user-role-modal/new-edit-user-role-modal.component';
import {ETableColDataType, ETableColFilterType} from '../../../../sdk/src/components/generic-table/table-col-def';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {EDeleteMode} from '../../../../sdk/src/enums/delete-mode.enum';
import {ECpmCUserRoleTypeApiEnum} from '../../../../gen/cpm-c-api-ts/src/enums/cpmc-sdk.api-enums';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {UserRoleUtils} from '../../utils/user-role.utils';

/**
 * UserRole CRUD table component
 */
@Component({
    selector: 'app-user-role-crud',
    templateUrl: './user-role-crud.component.html',
    imports: [PrimeNgImportsModule, SdkImportsModule]
})
export class UserRoleCRUDComponent extends IBaseCRUDComponent<
    CpmCUserRoleApiModel,
    NewEditUserRoleModalComponent,
    NewEditUserRoleModalConfig,
    NewEditUserRoleModalResult
> {
    protected readonly CpmCUserRoleApiService = CpmCUserRoleApiService;

    // ===============================================================
    // ===============================================================

    protected helpVisible = false;
    protected userRoleTypeDescriptions = UserRoleUtils.typeDescriptions;
    protected userRoleTypeTags = UserRoleUtils.getTypeTableTagDefs(this.generateColorFromString.bind(this));

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure({
            // Model config
            modelName: 'User Role',
            getModelHumanIdentifier: (model: CpmCUserRoleApiModel) => model.name,

            // Service config
            deleteMode: EDeleteMode.CONFIRM_REENFORCED,
            deleteModelService: (modelToDelete: CpmCUserRoleApiModel) =>
                CpmCUserRoleApiService.deleteUserRole(modelToDelete.uuid!),

            // Modal config
            modalType: NewEditUserRoleModalComponent,
            getDialogServiceParameters: NewEditUserRoleModalComponent.getDialogServiceParameters,
            modalExtendedConfigBuilder: (model?: CpmCUserRoleApiModel) =>
                new NewEditUserRoleModalConfig(model, this.isFromGlobalView),

            // Authorization config
            authorizationNew: CpmCUserRoleApiService.endpointNew(this.FAKE_UUID),
            authorizationEdit: CpmCUserRoleApiService.endpointEdit(this.FAKE_UUID, this.FAKE_UUID),
            authorizationDelete: CpmCUserRoleApiService.endpointDelete(this.FAKE_UUID, this.FAKE_UUID),
            commonAuthorizationNew: CpmCUserRoleApiService.endpointNew(undefined),
            commonAuthorizationEdit: CpmCUserRoleApiService.endpointEdit(undefined, this.FAKE_UUID),
            commonAuthorizationDelete: CpmCUserRoleApiService.endpointDelete(undefined, this.FAKE_UUID)
        });
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    public override async onReady(): Promise<void> {}
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On refresh (Error handling is done in the parent)
     */
    public override async onRefresh(): Promise<void> {
        this.loadingStr = 'Loading user roles...';

        const result = await firstValueFrom(
            CpmCUserRoleApiService.getUserRoles(
                this.isFromGlobalView ? {} : {type: ECpmCUserRoleTypeApiEnum.MULTI_SCHEMA_TENANT_ROLE},
                this.paginatorConfig
            )
        );

        this.items = result.data;
        this.totalItemCount = result.total;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On help button pressed
     */
    public override async onHelp(): Promise<void> {
        this.helpVisible = true;
    }

    // ===============================================================
    // ===============================================================

    /**
     * Init the columns (Error handling is done in the parent)
     */
    protected override async initColumns(): Promise<void> {
        this.tableCols = [
            {
                field: 'type',
                header: 'Type',
                dataType: ETableColDataType.TAGS,
                searcheable: false,
                filterable: true,
                filterType: ETableColFilterType.SELECT,
                filterSelectOptions: this.userRoleTypeTags,
                tags: this.userRoleTypeTags
            },
            {field: 'name', header: 'Name', searcheable: true, filterable: true},
            {field: 'description', header: 'Description', searcheable: true, filterable: true},
            {
                field: 'is_builtin',
                header: 'Is Built-in?',
                searcheable: true,
                filterable: true,
                filterType: ETableColFilterType.BOOLEAN,
                dataType: ETableColDataType.BOOLEAN_ICON
            }
        ];

        this.addCreatedColumns();
        this.addSharedModelContextualColumns();
    }
}
